import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface PageHeaderProps {
  readonly title: string;
  readonly description?: string;
  /** Already-rendered links; the composite draws the separators and the current page. */
  readonly breadcrumbs?: readonly { readonly label: string; readonly href?: string }[];
  /** The page's actions. One primary at most — the design system's rule, not a suggestion. */
  readonly actions?: ReactNode;
  readonly className?: string;
}

/**
 * The top of a page: where somebody is, what this is, and what they can do here.
 *
 * Every page in both applications writes its own `h1` and its own description paragraph, with
 * slightly different spacing each time. This is that, once — and it is where breadcrumbs belong,
 * which is the part most of those pages left out entirely.
 *
 * The title is an `h1` because there is one per page. A page with two first-level headings is a
 * page a screen-reader user cannot navigate by heading.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-(--space-xs)', className)}>
      {breadcrumbs === undefined || breadcrumbs.length === 0 ? null : (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-(--space-xs) text-(length:--font-size-caption) text-(--color-text-muted)">
            {breadcrumbs.map((crumb, index) => {
              const last = index === breadcrumbs.length - 1;
              return (
                <li key={crumb.label} className="flex items-center gap-(--space-xs)">
                  {crumb.href === undefined || last ? (
                    // The current page is marked, not linked: a link to where you already are is
                    // a keyboard stop that goes nowhere.
                    <span aria-current={last ? 'page' : undefined}>{crumb.label}</span>
                  ) : (
                    <a href={crumb.href} className="underline-offset-4 hover:underline">
                      {crumb.label}
                    </a>
                  )}
                  {last ? null : <span aria-hidden="true">/</span>}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <div className="flex flex-wrap items-start justify-between gap-(--space-sm)">
        <h1 className="text-(length:--font-size-heading) font-semibold text-(--color-text-primary)">
          {title}
        </h1>
        {actions === undefined ? null : (
          <div className="flex items-center gap-(--space-sm)">{actions}</div>
        )}
      </div>

      {description === undefined ? null : (
        <p className="max-w-(--width-container-narrow) text-(length:--font-size-body) text-(--color-text-secondary)">
          {description}
        </p>
      )}
    </div>
  );
}
